import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message, Switch, Tag } from "antd";
import { AdminTable } from "../Table/AntTable";
import type { DataTableColumn } from "../Table/AntTable";
import { getAllUsers } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";

type User = {
  id: string;
  name?: string;
  email: string;
  role?: string;
  verified?: boolean;
  isEnabled?: boolean;
  action?: string;
};

const ViewUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const fetchedUsers: User[] = await getAllUsers();
      const blacklistUsers = fetchedUsers.filter(
        (u) => u.email === "" || !u.email
      );
      if (blacklistUsers.length > 0) {
        console.warn(
          "Invalid email users found and excluded:",
          blacklistUsers.map((u) => {
            return { id: u.id, role: u.role };
          })
        );
      }
      setUsers(fetchedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  const computeDisplayName = useCallback((u: User): string => {
    const emailLocal = u.email ? u.email.split("@")[0] : "";
    const name = (u.name ?? "").trim();
    return name !== "" ? name : emailLocal;
  }, []);

  const computeRole = useCallback(
    (u: User): "admin" | "user" =>
      u.role?.toLowerCase() === "admin" ? "admin" : "user",
    []
  );

  const computeVerified = useCallback(
    (u: User): boolean => computeRole(u) === "admin" || (u.verified ?? false),
    [computeRole]
  );

  const toggleUserEnabled = useCallback(
    async (id: string, isEnabled: boolean) => {
      try {
        if (!user) {
          message.error("Not authenticated.");
          await fetchUsers();
          return;
        }

        const token = await user.getIdToken();
        const payloadObj = { uid: id, isEnabled };
        const payload = btoa(JSON.stringify(payloadObj));
        setLoading(true);

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/enable-user`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ payload }),
          }
        );
        const data = await res.json();

        if (data?.success === true) {
          const current = users.find((u) => u.id === id);
          const displayName = current
            ? computeDisplayName(current) || current.id
            : id;
          message.success(
            `${displayName} is now ${isEnabled ? "Enabled" : "Disabled"}.`
          );
        } else {
          const errMsg =
            data?.details || data?.error || "Failed to update user status.";
          message.error(errMsg);
        }
      } catch (error) {
        message.error("Unable to reach server. Please try again.");
        console.error("Error toggling user enabled state:", error);
      } finally {
        await fetchUsers();
        setLoading(false);
      }
    },
    [computeDisplayName, user, users]
  );

  const columns: DataTableColumn<User>[] = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        sorter: (a, b) =>
          computeDisplayName(a).localeCompare(computeDisplayName(b)),
        render: (_: string, record: User) => (
          <strong>{computeDisplayName(record)}</strong>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        sorter: (a, b) => (a.email ?? "").localeCompare(b.email ?? ""),
        render: (value: string) => (
          <a href={`mailto:${value}`} style={{ color: "#1677ff" }}>
            {value}
          </a>
        ),
      },
      {
        title: "Role",
        dataIndex: "role",
        sorter: (a, b) => computeRole(a).localeCompare(computeRole(b)),
        render: (_: string, record: User) => (
          <strong>{computeRole(record)}</strong>
        ),
      },
      {
        title: "Email Verified",
        dataIndex: "verified",
        width: 140,
        filters: [
          { text: "Verified", value: true },
          { text: "Unverified", value: false },
        ],
        onFilter: (value: boolean, record: User) =>
          computeVerified(record) === value,
        render: (_: boolean, record: User) => {
          const isV = computeVerified(record);
          return (
            <Tag color={isV ? "green" : "red"}>
              {isV ? "Verified" : "Unverified"}
            </Tag>
          );
        },
      },
      {
        title: "Login",
        dataIndex: "action",
        key: "action",
        width: 180,
        align: "center",
        render: (_: unknown, record: User) => (
          <Switch
            checked={record.isEnabled ?? true}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
            onChange={(checked) => toggleUserEnabled(record.id, checked)}
          />
        ),
      },
    ],
    [computeRole, computeVerified, toggleUserEnabled, computeDisplayName]
  );

  return (
    <div style={{ padding: "24px" }}>
      <AdminTable<User>
        columns={columns}
        loading={loading}
        data={users}
        rowKey="id"
        pageSize={10}
        searchable={true}
        searchPlaceholder="Search users by name or email..."
        bordered={true}
        size="middle"
        showSizeChanger={true}
        pageSizeOptions={[10, 20, 50, 100]}
      />
    </div>
  );
};

export default ViewUsers;
